// Shared three.js scene logic for the SyQi video-mode avatar. Pure
// three.js/TypeScript — no JSX, no React — so both VideoAvatar.web.tsx
// (react-three-fiber DOM Canvas) and VideoAvatar.native.tsx
// (react-three-fiber native Canvas + expo-gl) drive the exact same rig,
// camera framing, lighting, and lip-sync animation math. The GLB is loaded
// once (via fetch -> ArrayBuffer -> GLTFLoader.parse, identical on both
// platforms), the resulting scene is posed/framed, and an AvatarAnimator
// instance owns the per-frame morph-target / bone animation driven by
// play()/tick()/stop() calls from the parent (SyqiOverviewVideo), which is
// also the sole owner of audio playback — this module only ever animates to
// the clock it's handed.
import { Platform } from 'react-native';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { LipsyncFrame } from './videoAvatarTypes';

export const AVATAR_GLB_URL = `${process.env.EXPO_PUBLIC_API_BASE_URL ?? ''}/agent-solver-v4.glb`;

// GLTFLoader browser-sniffs with `navigator.userAgent.match(...)`. React
// Native HAS a `navigator` but no `userAgent`, so that call throws and takes
// the entire parse down ("Cannot read property 'match' of undefined").
if (Platform.OS !== 'web') {
  const nav = (globalThis as any).navigator;
  if (nav && typeof nav.userAgent !== 'string') nav.userAgent = 'react-native';
}

// expo-gl cannot sample the RGBA32F "bone texture" three.js uses for
// skeletal animation — skinned meshes come out invisible while plain meshes
// render fine. Half-float textures ARE supported, and bone matrices don't
// need 32-bit precision at human scale, so on native we swap the skeleton's
// bone texture to HalfFloat and keep a converted mirror in sync each frame.
// This keeps full skeletal animation (gestures, head turns) working.
if (Platform.OS !== 'web') {
  const SkeletonProto = THREE.Skeleton.prototype as any;
  if (!SkeletonProto.__syqiHalfFloatPatched) {
    SkeletonProto.__syqiHalfFloatPatched = true;

    const syncHalf = (skeleton: any) => {
      const src: Float32Array = skeleton.boneMatrices;
      const dst: Uint16Array = skeleton.__halfMatrices;
      if (!src || !dst) return;
      for (let i = 0; i < src.length; i += 1) dst[i] = THREE.DataUtils.toHalfFloat(src[i]);
      if (skeleton.boneTexture) skeleton.boneTexture.needsUpdate = true;
    };

    SkeletonProto.computeBoneTexture = function computeBoneTexture(this: any) {
      let size = Math.sqrt(this.bones.length * 4);
      size = Math.ceil(size / 4) * 4;
      size = Math.max(size, 4);

      const boneMatrices = new Float32Array(size * size * 4);
      boneMatrices.set(this.boneMatrices);
      this.boneMatrices = boneMatrices;

      this.__halfMatrices = new Uint16Array(size * size * 4);
      const texture = new THREE.DataTexture(
        this.__halfMatrices,
        size,
        size,
        THREE.RGBAFormat,
        THREE.HalfFloatType
      );
      texture.needsUpdate = true;
      this.boneTexture = texture;
      syncHalf(this);
      return this;
    };

    const originalUpdate = SkeletonProto.update;
    SkeletonProto.update = function update(this: any) {
      originalUpdate.call(this);
      if (this.__halfMatrices) syncHalf(this);
    };
  }
}

/** Lipsync frame key -> candidate source keys in the LipsyncFrame payload,
 * checked in order; first key present on the frame wins. */
const MORPH_TARGET_SOURCES: Record<string, string[]> = {
  jawOpen: ['jawOpen', 'mouthOpen', 'viseme_aa'],
  E: ['viseme_E', 'viseme_I'],
  mouthFunnel: ['viseme_O', 'mouthFunnel'],
  mouthPucker: ['viseme_U', 'mouthPucker'],
  PP: ['viseme_PP'],
  FF: ['viseme_FF'],
  TH: ['viseme_TH'],
  CH: ['viseme_CH', 'viseme_kk'],
  DD: ['viseme_DD', 'viseme_nn'],
  SS: ['viseme_SS'],
  RR: ['viseme_RR'],
  Eye_Blink_L: ['eyeBlinkLeft'],
  Eye_Blink_R: ['eyeBlinkRight'],
};

const EYE_TARGETS = new Set(['Eye_Blink_L', 'Eye_Blink_R']);
const ARM_ROTATION_X = 1.15;
const SMOOTH_FACTOR = 0.45;
const CLIP_END_PAD = 0.35;
const BLINK_DURATION = 0.16;
const BLINK_MIN_GAP = 2.4;
const BLINK_MAX_GAP = 4.5;

interface MorphPair {
  mesh: THREE.Mesh;
  index: number;
}

export interface AvatarRig {
  root: THREE.Object3D;
  head: THREE.Object3D | null;
  leftArm: THREE.Object3D | null;
  rightArm: THREE.Object3D | null;
  morphTargets: Map<string, MorphPair[]>;
}

/** GLTFLoader decodes a GLB's embedded textures with browser-only APIs
 * (createImageBitmap / ImageBitmapLoader / URL.createObjectURL), none of
 * which exist in React Native — without this the whole parse fails on
 * device. This plugin takes over `loadTexture` on native: it pulls each
 * image's bytes straight out of the GLB, writes them to a cache file, and
 * hands three.js a Texture whose "image" is a { localUri }, which expo-gl
 * decodes and uploads natively. Material wiring stays with GLTFLoader, so
 * every map lands in the right slot exactly as it does on web. */
function createNativeTexturePlugin(parser: any, cacheKey: string) {
  return {
    name: 'SYQI_native_textures',
    async loadTexture(textureIndex: number): Promise<THREE.Texture> {
      let texture: THREE.Texture;
      try {
        const { File, Paths } = require('expo-file-system');
        const json = parser.json;
        const textureDef = json.textures[textureIndex];
        const sourceIndex = textureDef.source;
        const imageDef = json.images[sourceIndex];

        let bytes: Uint8Array;
        if (imageDef.bufferView !== undefined) {
          const view = await parser.getDependency('bufferView', imageDef.bufferView);
          bytes = new Uint8Array(view);
        } else {
          const res = await fetch(imageDef.uri);
          bytes = new Uint8Array(await res.arrayBuffer());
        }

        const ext = imageDef.mimeType === 'image/png' ? 'png' : 'jpg';
        const file = new File(Paths.cache, `avatar-${cacheKey}-tex-${sourceIndex}.${ext}`);
        if (file.exists) file.delete();
        file.create();
        file.write(bytes);

        // Native texture upload, per expo-three's proven recipe: three must
        // be told `isDataTexture` so it hands the image to gl.texImage2D
        // verbatim — otherwise it takes the DOM-image path, uploads nothing,
        // and every textured mesh renders fully transparent (invisible).
        // expo-gl then decodes the file itself; it needs real dimensions.
        const { Asset } = require('expo-asset');
        const { Image } = require('react-native');
        const asset = Asset.fromURI(file.uri);
        await asset.downloadAsync();
        let texWidth = asset.width;
        let texHeight = asset.height;
        if (!texWidth || !texHeight) {
          const dims = await new Promise<{ width: number; height: number }>((res, rej) =>
            Image.getSize(asset.localUri ?? file.uri, (width: number, height: number) => res({ width, height }), rej)
          );
          texWidth = dims.width;
          texHeight = dims.height;
          asset.width = texWidth;
          asset.height = texHeight;
        }
        texture = new THREE.Texture();
        (texture as any).isDataTexture = true;
        texture.image = { data: asset, width: texWidth, height: texHeight };
      } catch (e: any) {
        // A texture must never take the whole avatar down — fall back to a
        // flat white map so geometry, rig and lip-sync still load.
        console.warn('[avatar] texture', textureIndex, 'failed:', e?.message, e?.stack);
        texture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, THREE.RGBAFormat);
      }

      texture.flipY = false;
      // Keep three off the power-of-two/mipmap resize path — it needs a
      // canvas, which RN doesn't have.
      texture.generateMipmaps = false;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.needsUpdate = true;
      parser.associations.set(texture, { textures: textureIndex });
      return texture;
    },
  };
}

/** Derives a filesystem-safe cache key from a GLB url (its basename, minus
 * extension, slugified) — used to namespace native download/texture cache
 * files per-deity so distinct GLB urls never collide on disk. */
function cacheKeyFromUrl(url: string): string {
  const basename = url.split('?')[0].split('/').pop() ?? 'avatar';
  const stem = basename.replace(/\.glb$/i, '');
  const slug = stem.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || 'avatar';
}

/** Loads the avatar GLB: fetch -> ArrayBuffer -> GLTFLoader.parse. Web uses
 * the browser's own image decoding; native routes textures through the
 * plugin above. */
export async function loadAvatarScene(url: string = AVATAR_GLB_URL): Promise<THREE.Object3D> {
  const cacheKey = cacheKeyFromUrl(url);
  let buffer: ArrayBuffer;
  if (Platform.OS === 'web') {
    const res = await fetch(url);
    buffer = await res.arrayBuffer();
  } else {
    // RN's fetch().arrayBuffer() marshals megabytes across the bridge and
    // stalls on a model this size. Download natively instead, and keep the
    // file so later launches skip the network entirely.
    const { File, Paths } = require('expo-file-system');
    const cached = new File(Paths.cache, `avatar-${cacheKey}.glb`);
    if (!cached.exists || (cached.size ?? 0) < 1024) {
      if (cached.exists) cached.delete();
      await File.downloadFileAsync(url, cached);
    }
    const bytes: Uint8Array = await cached.bytes();
    buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  }
  const loader = new GLTFLoader();
  if (Platform.OS !== 'web') loader.register((parser: any) => createNativeTexturePlugin(parser, cacheKey));
  return new Promise((resolve, reject) => {
    loader.parse(
      buffer,
      '',
      (gltf) => resolve(gltf.scene),
      (err: any) => {
        console.warn('[avatar] GLB parse failed:', err?.message, err?.stack);
        reject(new Error(err?.message ? `${err.message}` : 'GLB parse failed'));
      }
    );
  });
}

function findBone(root: THREE.Object3D, name: string): THREE.Object3D | null {
  let found: THREE.Object3D | null = null;
  root.traverse((obj) => {
    if (found) return;
    if (obj.name === name || obj.name === `mixamorig${name}`) found = obj;
  });
  return found;
}

/** Poses the loaded scene, zeroes out the GLB's default (non-neutral) morph
 * influences, finds the arm/head bones, and precomputes mesh+index pairs for
 * every morph target we drive. Call once, right after load. */
export function buildRig(root: THREE.Object3D): AvatarRig {
  const morphTargets = new Map<string, MorphPair[]>();
  for (const targetName of Object.keys(MORPH_TARGET_SOURCES)) morphTargets.set(targetName, []);

  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.morphTargetInfluences || !mesh.morphTargetDictionary) return;
    // The export ships nonzero default influences (e.g. eyes stuck closed) — reset.
    mesh.morphTargetInfluences.fill(0);
    for (const targetName of Object.keys(MORPH_TARGET_SOURCES)) {
      const index = mesh.morphTargetDictionary[targetName];
      if (index !== undefined) morphTargets.get(targetName)!.push({ mesh, index });
    }
  });

  const leftArm = findBone(root, 'LeftArm');
  const rightArm = findBone(root, 'RightArm');
  const head = findBone(root, 'Head');
  if (leftArm) leftArm.rotation.x += ARM_ROTATION_X;
  if (rightArm) rightArm.rotation.x += ARM_ROTATION_X;

  return { root, head, leftArm, rightArm, morphTargets };
}

/** expo-gl cannot render three.js SkinnedMeshes (the skeleton/bone-texture
 * path draws nothing — a plain Mesh in the same scene renders fine), so on
 * native we bake the posed skeleton into static geometry: each vertex is
 * pushed through its bone transform once on the CPU, then re-emitted as an
 * ordinary Mesh. Morph attributes ride along untouched, so lip-sync and
 * blinks still animate; only bone motion (head sway) is given up, which the
 * root-level idle motion covers. Call AFTER buildRig has posed the arms. */
export function bakeSkinning(root: THREE.Object3D): void {
  const swaps: Array<{ parent: THREE.Object3D; from: THREE.Object3D; to: THREE.Mesh }> = [];
  root.updateMatrixWorld(true);

  root.traverse((obj) => {
    const sm = obj as THREE.SkinnedMesh;
    if (!(sm as any).isSkinnedMesh || !sm.geometry?.attributes?.position) return;

    sm.skeleton?.update?.();
    const geometry = sm.geometry.clone();
    const position = geometry.attributes.position as THREE.BufferAttribute;
    const vertex = new THREE.Vector3();
    for (let i = 0; i < position.count; i += 1) {
      vertex.fromBufferAttribute(position, i);
      sm.applyBoneTransform(i, vertex);
      position.setXYZ(i, vertex.x, vertex.y, vertex.z);
    }
    position.needsUpdate = true;
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();

    const baked = new THREE.Mesh(geometry, sm.material as THREE.Material);
    baked.name = sm.name;
    baked.morphTargetInfluences = sm.morphTargetInfluences;
    baked.morphTargetDictionary = sm.morphTargetDictionary;
    baked.frustumCulled = false;
    if (sm.parent) swaps.push({ parent: sm.parent, from: sm, to: baked });
  });

  for (const { parent, from, to } of swaps) {
    parent.add(to);
    parent.remove(from);
  }
}

/** Frames the camera on the avatar's face using the loaded scene's bbox. */
export function frameCamera(camera: THREE.PerspectiveCamera, root: THREE.Object3D): void {
  camera.fov = 28;
  camera.updateProjectionMatrix();
  const box = new THREE.Box3().setFromObject(root);
  const size = new THREE.Vector3();
  box.getSize(size);
  const h = size.y;
  const cx = (box.min.x + box.max.x) / 2;
  const faceY = box.min.y + 0.86 * h;
  camera.position.set(cx, faceY - 0.015 * h, box.max.z + 0.42 * h);
  camera.lookAt(cx, faceY - 0.02 * h, 0);
}

/**
 * Procedural soft-cloud sky, generated as raw pixels into a DataTexture.
 * Deliberately NOT an image asset: this module runs under both RN-web and
 * the native stage, so a DOM <canvas> isn't always available, and an external
 * image would add a download plus a CORS surface for no visual gain. The PRNG
 * is seeded so every platform and reload produces the identical sky.
 * Kept in the neumorphic pale-blue palette so it reads as depth behind the
 * avatar rather than a competing background.
 */
export function makeCloudSkyTexture(size = 512): THREE.DataTexture {
  let seed = 1337;
  const rnd = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);

  const OCTAVES = 5;
  const grids: Array<{ n: number; g: Float32Array }> = [];
  for (let o = 0; o < OCTAVES; o++) {
    const n = 4 << o;
    const g = new Float32Array(n * n);
    for (let i = 0; i < g.length; i++) g[i] = rnd();
    grids.push({ n, g });
  }

  const smooth = (t: number) => t * t * (3 - 2 * t);
  const sampleGrid = ({ n, g }: { n: number; g: Float32Array }, u: number, v: number) => {
    const fx = u * n;
    const fy = v * n;
    const x0 = Math.floor(fx);
    const y0 = Math.floor(fy);
    const tx = smooth(fx - x0);
    const ty = smooth(fy - y0);
    const at = (x: number, y: number) => g[(((y % n) + n) % n) * n + (((x % n) + n) % n)];
    const a = at(x0, y0);
    const b = at(x0 + 1, y0);
    const c = at(x0, y0 + 1);
    const d = at(x0 + 1, y0 + 1);
    return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
  };

  // DataTexture rows run bottom-up, so v=0 is the horizon and v=1 the zenith.
  const SKY_TOP = [0xb2, 0xc8, 0xe8];
  const SKY_HORIZON = [0xe8, 0xee, 0xf8];
  const data = new Uint8Array(size * size * 4);

  for (let y = 0; y < size; y++) {
    const v = y / (size - 1);
    for (let x = 0; x < size; x++) {
      const u = x / (size - 1);

      let f = 0;
      let amp = 0.5;
      let norm = 0;
      for (let o = 0; o < OCTAVES; o++) {
        f += sampleGrid(grids[o], u, v) * amp;
        norm += amp;
        amp *= 0.5;
      }
      f /= norm;

      // Bias coverage toward the upper sky so the horizon stays clean behind
      // the avatar's shoulders and the face never competes with a cloud edge.
      const coverage = Math.max(0, Math.min(1, (f - (0.52 - v * 0.16)) / 0.26));
      const cloud = smooth(coverage) * 0.92;

      const i = (y * size + x) * 4;
      for (let c = 0; c < 3; c++) {
        const sky = SKY_HORIZON[c] + (SKY_TOP[c] - SKY_HORIZON[c]) * v;
        data[i + c] = Math.round(sky + (255 - sky) * cloud);
      }
      data[i + 3] = 255;
    }
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/** Adds the fixed three-light rig + cloud-sky background. */
export function addLights(scene: THREE.Scene): void {
  const ambient = new THREE.AmbientLight(0xffffff, 1.1);
  const directional = new THREE.DirectionalLight(0xffffff, 2.0);
  directional.position.set(0.6, 1.6, 1.4);
  const point = new THREE.PointLight(0x9fd0ff, 1.0);
  point.position.set(-0.8, 1.2, -0.6);
  scene.add(ambient, directional, point);
  scene.background = makeCloudSkyTexture();
}

function nowSec(): number {
  return (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function sourceValue(frame: LipsyncFrame, targetName: string): number {
  for (const key of MORPH_TARGET_SOURCES[targetName]) {
    const v = frame[key];
    if (typeof v === 'number') return v;
  }
  return 0;
}

/** Drives per-frame idle (blink/breathing/head-sway) and speaking (lip-sync
 * morph interpolation + drift correction) animation for one rig. Fully
 * platform-agnostic — the platform Canvas components just call update()
 * every frame from their render loop. */
export class AvatarAnimator {
  private rig: AvatarRig;
  private onEnded?: (id: string) => void;

  private speaking = false;
  private clipId: string | null = null;
  private lipsync: LipsyncFrame[] = [];
  private lastFrameTime = 0;
  private startPerf = 0;
  private offsetCorr = 0;

  private morphCurrent = new Map<string, number>();
  private nextBlinkAt = 0;
  private blinkStart: number | null = null;

  constructor(rig: AvatarRig, onEnded?: (id: string) => void) {
    this.rig = rig;
    this.onEnded = onEnded;
  }

  play(id: string, lipsync: LipsyncFrame[], startAt = 0): void {
    this.clipId = id;
    this.lipsync = [...lipsync].sort((a, b) => a.time - b.time);
    this.lastFrameTime = this.lipsync.length ? this.lipsync[this.lipsync.length - 1].time : 0;
    this.startPerf = nowSec() - startAt;
    this.offsetCorr = 0;
    this.speaking = true;
  }

  /** Resyncs the animation clock to the parent's audio-playback clock. */
  tick(t: number): void {
    if (!this.speaking) return;
    const clipTime = nowSec() - this.startPerf + this.offsetCorr;
    this.offsetCorr += (t - clipTime) * 0.5;
  }

  stop(): void {
    this.speaking = false;
    this.clipId = null;
    this.lipsync = [];
  }

  private scheduleNextBlink(from: number): void {
    this.nextBlinkAt = from + BLINK_MIN_GAP + Math.random() * (BLINK_MAX_GAP - BLINK_MIN_GAP);
  }

  private sampleLipsync(clipTime: number): Record<string, number> {
    const frames = this.lipsync;
    const result: Record<string, number> = {};
    if (frames.length === 0) return result;

    let lo = 0;
    let hi = frames.length - 1;
    let idx = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (frames[mid].time <= clipTime) {
        idx = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    const a = frames[idx];
    const b = frames[idx + 1] ?? a;
    const span = b.time - a.time;
    const alpha = span > 0 ? Math.min(1, Math.max(0, (clipTime - a.time) / span)) : 0;

    for (const targetName of Object.keys(MORPH_TARGET_SOURCES)) {
      result[targetName] = lerp(sourceValue(a, targetName), sourceValue(b, targetName), alpha);
    }
    return result;
  }

  /** Call once per render frame. `elapsed` is the scene clock in seconds
   * (monotonic, e.g. r3f's `clock.getElapsedTime()`); used for idle
   * breathing/sway/blink so those keep animating even while not speaking. */
  update(elapsed: number): void {
    if (this.rig.root) this.rig.root.position.y = Math.sin(elapsed * 1.1) * 0.004;

    let frameValues: Record<string, number> = {};
    if (this.speaking) {
      const clipTime = nowSec() - this.startPerf + this.offsetCorr;
      if (this.lipsync.length === 0 || clipTime > this.lastFrameTime + CLIP_END_PAD) {
        const endedId = this.clipId;
        this.stop();
        if (endedId) this.onEnded?.(endedId);
      } else {
        frameValues = this.sampleLipsync(clipTime);
      }
    }

    if (this.rig.head) {
      if (this.speaking) {
        this.rig.head.rotation.y = Math.sin(elapsed * 0.9) * 0.045;
        this.rig.head.rotation.x = Math.sin(elapsed * 0.7 + 1.7) * 0.03;
      } else {
        this.rig.head.rotation.y = Math.sin(elapsed * 0.4) * 0.02;
        this.rig.head.rotation.x = Math.sin(elapsed * 0.33 + 1.7) * 0.012;
      }
    }

    let idleBlink = 0;
    if (!this.speaking) {
      if (elapsed >= this.nextBlinkAt) {
        if (this.blinkStart === null) this.blinkStart = elapsed;
        const bt = elapsed - this.blinkStart;
        if (bt <= BLINK_DURATION) {
          idleBlink = Math.sin((bt / BLINK_DURATION) * Math.PI);
        } else {
          this.blinkStart = null;
          this.scheduleNextBlink(elapsed);
        }
      }
    }

    for (const [targetName, pairs] of this.rig.morphTargets) {
      const desired = this.speaking
        ? (frameValues[targetName] ?? 0)
        : EYE_TARGETS.has(targetName)
          ? idleBlink
          : 0;
      const current = this.morphCurrent.get(targetName) ?? 0;
      const next = current + (desired - current) * SMOOTH_FACTOR;
      this.morphCurrent.set(targetName, next);
      for (const { mesh, index } of pairs) {
        if (mesh.morphTargetInfluences) mesh.morphTargetInfluences[index] = next;
      }
    }
  }
}
